import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import { cateringMap, dailyMealsMap, dbKeyMap, domainMap, firebaseConfig, menuMap, SCHOOL_LOCALHOST } from '../../functions/src/api/dbConfig';
import { installBenignAuthPersistenceRejectionHandler } from './authPersistence';

//@ts-ignore
window.firebase = firebase;

const ADMIN_SCHOOL_KEY_SESSION = 'adminSchoolKey';

export function getAdminSchoolKey() {
    if (typeof window === 'undefined')
        return null;
    return sessionStorage.getItem(ADMIN_SCHOOL_KEY_SESSION) || localStorage.getItem('schoolKey');
}

export function primeAdminSchoolKeyFromLegacyStorage() {
    if (typeof window === 'undefined')
        return;
    const legacySchoolKey = localStorage.getItem('schoolKey');
    !sessionStorage.getItem(ADMIN_SCHOOL_KEY_SESSION) && legacySchoolKey && sessionStorage.setItem(ADMIN_SCHOOL_KEY_SESSION, legacySchoolKey);
}

/** `?school=` on /admin — only when this tab has no school yet (e.g. new tab from middle-click). */
export function primeAdminSchoolKeyFromUrl() {
    if (typeof window === 'undefined' || !window.location.pathname.startsWith('/admin'))
        return;
    if (sessionStorage.getItem(ADMIN_SCHOOL_KEY_SESSION))
        return;
    const schoolFromUrl = new URLSearchParams(window.location.search).get('school');
    schoolFromUrl && sessionStorage.setItem(ADMIN_SCHOOL_KEY_SESSION, schoolFromUrl);
}

export function setAdminDomain(schoolKey: string) {

    if (!!schoolKey && schoolKey.length > 0) {
        sessionStorage.setItem(ADMIN_SCHOOL_KEY_SESSION, schoolKey);
        const params = new URLSearchParams(window.location.search);
        params.set('school', schoolKey);
        const search = params.toString();
        const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
        window.location.assign(nextUrl);
    }
}

const domain = (() => {
    if (window.location.pathname.startsWith('/admin')) {
        primeAdminSchoolKeyFromUrl();
        primeAdminSchoolKeyFromLegacyStorage();
        const schoolKey = getAdminSchoolKey();
        // let domain = null;
        // mainDatabase.ref(SCHOOLS_PATH).child(schoolKey).child('schoolDomain').once('value').then(
        //     s => {
        //         domain = s.val();
        //         console.log(domain);
        //     });
        // console.log('Admin setting domain to: ', domain);
        const domain = dbKeyMap[schoolKey];
        return domain;
    }

    const hostname = window.location.hostname;

    // Check if we're running locally
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
        return SCHOOL_LOCALHOST;
    }

    return hostname;
})();

// domainMap['127.0.0.2'] = domainMap[subDomainAthensCollegePrimaryAthens];

export const currentSchoolKey = !domain || domain === SCHOOL_LOCALHOST ? SCHOOL_LOCALHOST : Object.keys(dbKeyMap).find(key => dbKeyMap[key] === domain) ?? SCHOOL_LOCALHOST;
export const currentDb: { databaseURL: string } = !domain || domain === SCHOOL_LOCALHOST ? domainMap.localhost : domainMap[domain] ?? domainMap.default;
export const currentCatering = cateringMap[currentSchoolKey] ?? cateringMap.default;
export const currentMenu = menuMap[currentSchoolKey] ?? menuMap.default;
export const currentDailyMeals = dailyMealsMap[currentSchoolKey] ?? dailyMealsMap.default;

export const app = firebase.initializeApp(firebaseConfig);

if (process.env.NODE_ENV !== 'production') {        // For debuging
    (window as any).app = app;
}

export default firebase;

export const auth = firebase.auth();
installBenignAuthPersistenceRejectionHandler();
export const functions = firebase.app().functions('us-central1');
/**
 * Checkout → Viva redirect callables (`initiateOrderCheckout`, `prepareOrderPayment`, `finalizeOrderPayment`,
 * `cancelOrderPaymentPrep`) are deployed in europe-west1 next to the RTDB instances — see
 * PAYMENT_START_CALLABLE_OPTIONS in functions/src/index.ts. Calling them through `functions` (us-central1) returns not-found.
 */
export const functionsEu = firebase.app().functions('europe-west1');
export const storage = firebase.storage();

export const mainDatabase = firebase.database();
export const schoolDatabase = getDatabase(currentDb.databaseURL);
export function getDatabase(url: string) {
    if (url === domainMap.default.databaseURL) {
        // console.warn('Trying to initialize the default database for a second time!');
        return mainDatabase;
    } else {
        return app.database(url);
    }

}

if (process.env.NODE_ENV !== 'production') {
    Object.assign(window, {
        mainDatabase,
        schoolDatabse: schoolDatabase,
    })
}

/** Local Functions emulator only — RTDB/auth stay on production (localhost school DB). Set USE_FUNCTIONS_EMULATOR=1. */
if (process.env.USE_FUNCTIONS_EMULATOR === 'true') {
    const emulatorHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const emulatorPort = typeof window !== 'undefined' && window.location.port
        ? Number(window.location.port)
        : 5000;
    console.log(`Using Functions emulator via dev proxy at ${emulatorHost}:${emulatorPort} (database/auth unchanged)`);
    functions.useEmulator(emulatorHost, emulatorPort);
    functionsEu.useEmulator(emulatorHost, emulatorPort);
}

export interface DataSnapshot<T = any> extends firebase.database.DataSnapshot {
    val(): T
}
